import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer } from 'core/components';
import $syn from 'app/stores/synthetics/$syn';
import AgentDetails from 'app/views/synthetics/agents/AgentDetails';
import { SynthAgentDrawerContext } from './synthAgentDrawerContext';

export { useSynthAgentDrawer } from './synthAgentDrawerContext';

interface SynthAgentDrawerProviderProps {
  children: React.ReactNode;
  /**
   * When supplied, the provider hands the agent id to the page instead of rendering its own
   * overlay drawer. Lets a page route agent details through `Page`'s drawer, which is the only
   * one that honors the user's dock/location layout preferences.
   */
  onOpenAgent?: (agentId: string | number) => void;
}

/**
 * Synth-scoped provider that renders a single right-side Drawer with agent details
 * (summary + tests). Any AgentDisplay rendered inside can open it via context, so we avoid
 * threading props through the legend/table. Intentionally scoped to synth pages for now;
 * see thoughts/shared/plans/2026-08-11-synth-collapsible-panels-agent-popover-drawer.md
 * for the global-store rollout.
 */
export const SynthAgentDrawerProvider = observer(({ children, onOpenAgent }: SynthAgentDrawerProviderProps) => {
  const [openAgentId, setOpenAgentId] = useState<string | number | null>(null);

  const openAgentDrawer = useCallback(
    (agentId: string | number) => (onOpenAgent ? onOpenAgent(agentId) : setOpenAgentId(agentId)),
    [onOpenAgent]
  );
  const close = useCallback(() => setOpenAgentId(null), []);
  const value = useMemo(() => ({ openAgentDrawer, canOpen: true }), [openAgentDrawer]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const agents = $syn.agents as any;
  const result = openAgentId != null && agents ? agents.get(openAgentId) : null;
  const model = Array.isArray(result) ? result[0] : result;

  return (
    <SynthAgentDrawerContext.Provider value={value}>
      {children}
      {!onOpenAgent && (
        <Drawer isOpen={!!model} onClose={close} size={480} position="right" title="Agent Details" footer={null}>
          {model && <AgentDetails model={model} visibleTabs={['summary', 'tests']} hideActions drawerOnClose={close} />}
        </Drawer>
      )}
    </SynthAgentDrawerContext.Provider>
  );
});

export default SynthAgentDrawerProvider;
